import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Row, Col, Modal, Spinner } from 'react-bootstrap';
import { FaUser, FaKey, FaExclamationTriangle } from 'react-icons/fa';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from './common/header';
import apiService from '../services/apiService';

const UserProfilePage = () => {
    const { user, setUser, setToken } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        chave: ''
    });

    const [senhaData, setSenhaData] = useState({
        novaSenha: '',
        confirmarSenha: ''
    });

    const [loading, setLoading] = useState(false);
    const [loadingSenha, setLoadingSenha] = useState(false);
    const [showNovaSenha, setShowNovaSenha] = useState(false);
    const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);
    const [showSenhaAtual, setShowSenhaAtual] = useState(false);
    const [modernAlert, setModernAlert] = useState({ show: false, type: '', message: '' });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [senhaAtual, setSenhaAtual] = useState('');
    const [acaoPendente, setAcaoPendente] = useState(null); // 'perfil' ou 'senha'
    const [loadingPage, setLoadingPage] = useState(true);

    const showModernAlert = (message, type = 'success') => {
        setModernAlert({ show: true, type, message });
        setTimeout(() => {
            setModernAlert({ show: false, type: '', message: '' });
        }, 5000);
    };

    useEffect(() => {
        // Buscar dados atualizados do servidor
        const carregarDadosUsuario = async () => {
            try {
                setLoadingPage(true);
                const response = await apiService.getCurrentUser();
                if (response.user) {
                    setUser(response.user);
                    setFormData({
                        nome: response.user.nome || '',
                        email: response.user.email || '',
                        telefone: response.user.telefone || '',
                        chave: response.user.chave || ''
                    });
                }
            } catch (error) {
                console.error('Erro ao carregar dados do usuário:', error);
                // Se falhar, usar dados do context
                if (user) {
                    setFormData({
                        nome: user.nome || '',
                        email: user.email || '',
                        telefone: user.telefone || '',
                        chave: user.chave || ''
                    });
                }
            } finally {
                setLoadingPage(false);
            }
        };

        carregarDadosUsuario();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleSenhaChange = (field, value) => {
        setSenhaData({ ...senhaData, [field]: value });
    };

    const handleAplicarAlteracoes = () => {
        if (!formData.nome || !formData.email) {
            showModernAlert('Nome e email são obrigatórios.', 'error');
            return;
        }
        setAcaoPendente('perfil');
        setShowConfirmModal(true);
    };

    const confirmarAtualizacaoPerfil = async () => {
        try {
            setLoading(true);
            setShowConfirmModal(false);

            const response = await apiService.atualizarPerfil({
                ...formData,
                senhaAtual
            });

            if (response.success) {
                setUser(response.user);
                if (response.token) {
                    setToken(response.token);
                }
                showModernAlert('Informações atualizadas com sucesso!', 'success');
                setSenhaAtual('');
            }
        } catch (error) {
            showModernAlert(error.message || 'Erro ao atualizar informações.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAlterarSenha = () => {
        if (!senhaData.novaSenha || !senhaData.confirmarSenha) {
            showModernAlert('Preencha os dois campos de senha.', 'error');
            return;
        }

        if (senhaData.novaSenha !== senhaData.confirmarSenha) {
            showModernAlert('As senhas não coincidem.', 'error');
            return;
        }

        setAcaoPendente('senha');
        setShowConfirmModal(true);
    };

    const confirmarAlteracaoSenha = async () => {
        try {
            setLoadingSenha(true);
            setShowConfirmModal(false);

            const response = await apiService.alterarSenha({
                senhaAtual,
                novaSenha: senhaData.novaSenha
            });

            if (response.success) {
                showModernAlert('Senha alterada com sucesso!', 'success');
                setSenhaData({ novaSenha: '', confirmarSenha: '' });
                setSenhaAtual('');
            }
        } catch (error) {
            showModernAlert(error.message || 'Erro ao alterar senha.', 'error');
        } finally {
            setLoadingSenha(false);
        }
    };

    const handleConfirmarAcao = () => {
        if (!senhaAtual) {
            showModernAlert('Digite sua senha atual.', 'error');
            return;
        }

        if (acaoPendente === 'perfil') {
            confirmarAtualizacaoPerfil();
        } else if (acaoPendente === 'senha') {
            confirmarAlteracaoSenha();
        }
    };

    const handleCancelarModal = () => {
        setShowConfirmModal(false);
        setSenhaAtual('');
        setAcaoPendente(null);
    };

    if (loadingPage) {
        return (
            <div className="h-screen flex flex-col overflow-hidden">
                <Header />
                <div className="d-flex justify-content-center align-items-center" style={{ flex: 1 }}>
                    <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Carregando...</span>
                    </Spinner>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            <Header />

            {/* Modal de Confirmação */}
            <Modal show={showConfirmModal} onHide={handleCancelarModal} size="md" centered>
                <Modal.Header
                    className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex items-center"
                    style={{
                        backgroundColor: '#6c757d',
                        color: 'white',
                        padding: '12px 20px',
                        borderRadius: '8px 8px 0 0',
                        marginBottom: '0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}
                >
                    <span style={{ fontWeight: 'bold' }}>Alerta</span>
                    <FaExclamationTriangle />
                </Modal.Header>

                <Modal.Body style={{ padding: '30px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '25px', lineHeight: '1.6' }}>
                        <p style={{ margin: '0 0 20px 0', fontSize: '16px' }}>
                            Digite sua senha atual para confirmar alterações:
                        </p>
                        <div style={{ position: 'relative', maxWidth: '400px', margin: '0 auto' }}>
                            <Form.Control
                                type={showSenhaAtual ? "text" : "password"}
                                placeholder="Senha atual"
                                value={senhaAtual}
                                onChange={(e) => setSenhaAtual(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleConfirmarAcao();
                                    }
                                }}
                                autoFocus
                                style={{ paddingRight: '40px' }}
                            />
                            <Button
                                variant="link"
                                onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'none',
                                    padding: '0',
                                    cursor: 'pointer',
                                    color: '#6c757d',
                                    zIndex: 10
                                }}
                                aria-label={showSenhaAtual ? "Ocultar senha" : "Mostrar senha"}
                            >
                                {showSenhaAtual ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                            </Button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '15px' }}>
                        <Button
                            variant="danger"
                            onClick={handleCancelarModal}
                            disabled={loading || loadingSenha}
                            style={{
                                backgroundColor: '#dc3545',
                                borderColor: '#dc3545',
                                padding: '10px 30px',
                                fontWeight: 'bold',
                                borderRadius: '8px',
                                minWidth: '100px'
                            }}
                        >
                            Voltar
                        </Button>

                        <Button
                            variant="primary"
                            onClick={handleConfirmarAcao}
                            disabled={loading || loadingSenha}
                            style={{
                                backgroundColor: '#003B6B',
                                borderColor: '#003B6B',
                                padding: '10px 30px',
                                fontWeight: 'bold',
                                borderRadius: '8px',
                                minWidth: '100px'
                            }}
                        >
                            {loading || loadingSenha ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Processando...
                                </>
                            ) : (
                                'Confirmar'
                            )}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modern Alert Notification */}
            {modernAlert.show && (
                <div
                    style={{
                        position: 'fixed',
                        top: '2rem',
                        right: '2rem',
                        zIndex: 9999,
                        maxWidth: '400px',
                        background: modernAlert.type === 'success' ? '#003B6B' : '#dc3545',
                        color: 'white',
                        padding: '1rem 1.5rem',
                        borderRadius: '1rem',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        animation: 'slideInRight 0.5s ease-out',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                    }}
                >
                    <div style={{
                        width: '2rem',
                        height: '2rem',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        {modernAlert.type === 'success' ? '✓' : '✕'}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', marginBottom: '0.25rem' }}>
                            {modernAlert.type === 'success' ? 'Sucesso!' : 'Erro!'}
                        </div>
                        <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>
                            {modernAlert.message}
                        </div>
                    </div>
                    <button
                        onClick={() => setModernAlert({ show: false, type: '', message: '' })}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            borderRadius: '0.25rem',
                            opacity: 0.7,
                            fontSize: '1.2rem',
                            lineHeight: 1,
                            transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = 1}
                        onMouseLeave={(e) => e.target.style.opacity = 0.7}
                    >
                        ×
                    </button>
                </div>
            )}

            <Container fluid className="flex-1 bg-gray-50 overflow-auto" style={{ backgroundColor: '#f8f9fa', paddingTop: '40px', paddingBottom: '40px' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    {/* Botão Voltar no topo */}
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Button
                            variant="primary"
                            onClick={() => navigate('/')}
                            style={{
                                backgroundColor: '#003B6B',
                                borderColor: '#003B6B',
                                padding: '10px 40px',
                                fontWeight: 'bold',
                                borderRadius: '8px'
                            }}
                        >
                            Voltar
                        </Button>
                    </div>

                    {/* Card Informações de cadastro */}
                    <div style={{ marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <div
                            className="bg-gradient-to-r from-slate-700 to-slate-600"
                            style={{
                                padding: '15px 20px',
                                color: 'white'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FaUser size={20} />
                                <h5 style={{ fontWeight: 'bold', margin: 0 }}>Informações de cadastro</h5>
                            </div>
                        </div>

                        <div style={{ backgroundColor: 'white', padding: '30px' }}>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontWeight: 'bold' }}>Nome</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.nome}
                                            onChange={(e) => handleInputChange('nome', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontWeight: 'bold' }}>E-mail</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontWeight: 'bold' }}>Telefone</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.telefone}
                                            onChange={(e) => handleInputChange('telefone', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontWeight: 'bold' }}>Chave</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.chave}
                                            onChange={(e) => handleInputChange('chave', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mt-3">
                                <Col className="d-flex justify-content-center">
                                    <Button
                                        variant="primary"
                                        onClick={handleAplicarAlteracoes}
                                        disabled={loading}
                                        style={{
                                            backgroundColor: '#003B6B',
                                            borderColor: '#003B6B',
                                            padding: '10px 30px',
                                            fontWeight: 'bold',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        {loading ? 'Salvando...' : 'Aplicar alterações'}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Card Senha */}
                    <div style={{ marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <div
                            className="bg-gradient-to-r from-slate-700 to-slate-600"
                            style={{
                                padding: '15px 20px',
                                color: 'white'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FaKey size={20} />
                                <h5 style={{ fontWeight: 'bold', margin: 0 }}>Senha</h5>
                            </div>
                        </div>

                        <div style={{ backgroundColor: 'white', padding: '30px' }}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontWeight: 'bold' }}>Nova senha</Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showNovaSenha ? "text" : "password"}
                                                value={senhaData.novaSenha}
                                                onChange={(e) => handleSenhaChange('novaSenha', e.target.value)}
                                                style={{ paddingRight: '40px' }}
                                            />
                                            <Button
                                                variant="link"
                                                onClick={() => setShowNovaSenha(!showNovaSenha)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '10px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    border: 'none',
                                                    background: 'none',
                                                    padding: '0',
                                                    cursor: 'pointer',
                                                    color: '#6c757d',
                                                    zIndex: 10
                                                }}
                                                aria-label={showNovaSenha ? "Ocultar senha" : "Mostrar senha"}
                                            >
                                                {showNovaSenha ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                                            </Button>
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontWeight: 'bold' }}>Confirmar nova senha</Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showConfirmarSenha ? "text" : "password"}
                                                value={senhaData.confirmarSenha}
                                                onChange={(e) => handleSenhaChange('confirmarSenha', e.target.value)}
                                                style={{ paddingRight: '40px' }}
                                            />
                                            <Button
                                                variant="link"
                                                onClick={() => setShowConfirmarSenha(!showConfirmarSenha)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '10px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    border: 'none',
                                                    background: 'none',
                                                    padding: '0',
                                                    cursor: 'pointer',
                                                    color: '#6c757d',
                                                    zIndex: 10
                                                }}
                                                aria-label={showConfirmarSenha ? "Ocultar senha" : "Mostrar senha"}
                                            >
                                                {showConfirmarSenha ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                                            </Button>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col className="d-flex justify-content-center">
                                    <Button
                                        variant="primary"
                                        onClick={handleAlterarSenha}
                                        disabled={loadingSenha}
                                        style={{
                                            backgroundColor: '#003B6B',
                                            borderColor: '#003B6B',
                                            padding: '10px 30px',
                                            fontWeight: 'bold',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        {loadingSenha ? 'Alterando...' : 'Alterar senha'}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default UserProfilePage;
